import { createBinnedDataBufferFromData } from 'features/dataLayers/geckoBinnedDataBuffer'
import { createDfnCategoryCdf } from 'features/dataLayers/generation/dfnCategory/dfnCategoryLayer'
import { flatten2DValues } from 'features/dataLayers/generation/flatten2DValues'
import { DfnCategoryWorkerProps } from 'features/dataLayers/generation/workers/calculateDfnCategoryData.worker'
import DfnCategoryWorker from 'features/dataLayers/generation/workers/calculateDfnCategoryData.worker?worker'
import { useBinnedDataBufferCache } from 'features/dataLayers/load/useBinnedDataBufferCache'
import { useGetCdf2dDataFetcher } from 'features/dataLayers/load/useCdfRawValues2DArray'
import memoize from 'lodash/memoize'
import { useCallback, useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoInspection,
} from 'types'

export function useGenerateDfnCategoryCDFs(
  assetModel: GeckoAssetModel | undefined,
  differenceFromNominalCdfs: ReadonlyArray<GeckoComponentDataFrame> | undefined,
  inspections: ReadonlyArray<GeckoInspection>
): ReadonlyArray<GeckoComponentDataFrame> {
  const fetchCdf2dArray = useGetCdf2dDataFetcher()
  const { putBinnedDataBufferInCache } = useBinnedDataBufferCache()

  const classifyDifferenceFromNominal = useCallback(
    (cdf: GeckoComponentDataFrame) =>
      new Promise<Float32Array>((resolve, reject) => {
        fetchCdf2dArray(cdf).then(
          (differenceFromNominalValues) => {
            if (!differenceFromNominalValues) {
              reject(new Error('No difference-from-nominal values returned'))
              return
            }

            const worker = new DfnCategoryWorker()
            const workerProps: DfnCategoryWorkerProps = {
              componentId: cdf.tags.component ?? '',
              differenceFromNominalValues,
              binHeightJson: cdf.data.binning.bin_size.height.toJSON(),
              binWidthJson: cdf.data.binning.bin_size.width.toJSON(),
              yGeometryStartJson: cdf.data.geometry.start.y.toJSON(),
              xGeometryStartJson: cdf.data.geometry.start.x.toJSON(),
            }

            worker.onmessage = (message: MessageEvent<number[][]>) => {
              worker.terminate()
              resolve(flatten2DValues(message.data))
            }
            worker.onerror = (error) => {
              worker.terminate()
              reject(error)
            }
            worker.postMessage(workerProps)
          },
          (error: unknown) => {
            reject(
              error instanceof Error
                ? error
                : new Error('Could not load difference-from-nominal values')
            )
          }
        )
      }),
    [fetchCdf2dArray]
  )

  return useMemo(() => {
    if (!differenceFromNominalCdfs?.length) return []

    return differenceFromNominalCdfs.flatMap((differenceFromNominalCdf) => {
      const inspection = inspections.find(
        (candidate) => candidate.id === differenceFromNominalCdf.tags.inspection
      )
      const categoryCdf = createDfnCategoryCdf({
        differenceFromNominalCdf,
        assetModel,
        inspection,
      })
      if (!categoryCdf) return []

      const loadValues = memoize(async () =>
        createBinnedDataBufferFromData(
          await classifyDifferenceFromNominal(differenceFromNominalCdf),
          categoryCdf
        )
      )
      putBinnedDataBufferInCache({
        id: categoryCdf.id,
        generation: categoryCdf.generation,
        data: loadValues,
      })
      return [categoryCdf]
    })
  }, [
    assetModel,
    classifyDifferenceFromNominal,
    differenceFromNominalCdfs,
    inspections,
    putBinnedDataBufferInCache,
  ])
}
